import { Box, Flex, FormLabel, Switch, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { SelectControl } from "formik-chakra-ui";
import React from "react";
import { LocationBoundariesFilterRuleOptions } from "src/schemas/formTemplateFilters";
import * as AF from "src/types/formTemplate";
import { z } from "zod";

type Props = {
  questionId: uuid;
  disabled?: boolean;
};
export const FormikSchema = z.object({
  locationBoundaries: z.discriminatedUnion("switch", [
    z.object({
      switch: z.literal("on"),
      rule: z.string().superRefine((value: string, ctx) => {
        if (
          !(
            Object.values(LocationBoundariesFilterRuleOptions) as string[]
          ).includes(value)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid location boundaries",
          });
        }
      }),
    }),
    z.object({
      switch: z.literal("off"),
      rule: z.string(),
    }),
  ]),
});

export type FormikType = z.infer<typeof FormikSchema>;
export const EmptyFormValue: FormikType = {
  locationBoundaries: {
    switch: "off",
    rule: LocationBoundariesFilterRuleOptions.Filter,
  },
};
export function toFormValue(question: AF.V2.Question): FormikType {
  if (question.type !== "Address") {
    return EmptyFormValue;
  }

  const locationBoundariesFilter = question.filters?.find(
    (filter) => filter.type === "LocationBoundariesFilter"
  );
  if (locationBoundariesFilter === undefined) {
    return EmptyFormValue;
  }

  switch (locationBoundariesFilter.config.rule) {
    case "Filter":
      return {
        locationBoundaries: {
          switch: "on",
          rule: LocationBoundariesFilterRuleOptions.Filter,
        },
      };
    case "ShowFirst":
      return {
        locationBoundaries: {
          switch: "on",
          rule: LocationBoundariesFilterRuleOptions.ShowFirst,
        },
      };

    default:
      const _exhaustiveCheck: never = locationBoundariesFilter.config.rule;
      throw new Error(
        `Unhandled boundaries filter rule type: ${locationBoundariesFilter.config.rule}`
      );
  }
}

export function toAddressQuestion(
  values: FormikType
): Pick<AF.V2.AddressQuestion, "filters"> {
  if (values.locationBoundaries.switch === "off") {
    return { filters: undefined };
  }

  return {
    filters: [
      {
        type: "LocationBoundariesFilter",
        config: {
          rule: values.locationBoundaries.rule as "Filter" | "ShowFirst",
        },
      },
    ],
  };
}

export const ConfigureLocationBoundaries: React.FC<Props> = ({
  questionId,
  disabled,
}) => {
  const formik = useFormikContext<FormikType>();

  React.useEffect(() => {
    (async () => {
      if (disabled) {
        await formik.setFieldValue("locationBoundaries.switch", "off");
      }
    })();
  }, [disabled, formik]);

  return (
    <Flex direction="column" width="70%" gap="4">
      <Flex direction="column">
        <Text mb={1}>Location boundaries</Text>
        <FormLabel
          htmlFor={`locationBoundaries${questionId}`}
          display="flex"
          flexDirection="row"
          gap="2"
          margin={0}
          mb={1}
        >
          <Switch
            id={`locationBoundaries${questionId}`}
            isChecked={formik.values.locationBoundaries.switch === "on"}
            onChange={async () => {
              if (formik.values.locationBoundaries.switch === "on") {
                await formik.setFieldValue("locationBoundaries.switch", "off");
              } else {
                await formik.setFieldValue("locationBoundaries.switch", "on");
              }
            }}
            isDisabled={disabled}
          />
          <Box>
            {formik.values.locationBoundaries.switch === "on" ? "On" : "Off"}
          </Box>
        </FormLabel>

        {disabled ? (
          <Text fontSize="sm" fontWeight="400" color="gray.600">
            There's already an Address question with Location boundaries turned
            on
          </Text>
        ) : null}
        {!disabled && formik.values.locationBoundaries.switch === "on" ? (
          <Text fontSize="sm" fontWeight="400" color="gray.600">
            The answer to this question can be used to affect displayed schools
            based on configured school boundaries.
          </Text>
        ) : null}

        {!disabled && formik.values.locationBoundaries.switch === "off" ? (
          <Text fontSize="sm" fontWeight="400" color="gray.600">
            The answer to this question will not affect displayed schools based
            on configured school boundaries.
          </Text>
        ) : null}
      </Flex>

      {formik.values.locationBoundaries.switch === "on" && (
        <>
          <SelectControl label="" name="locationBoundaries.rule">
            <option value={LocationBoundariesFilterRuleOptions.Filter}>
              Hide schools outside of boundaries
            </option>
            <option value={LocationBoundariesFilterRuleOptions.ShowFirst}>
              Sort schools within boundaries to the top of the list
            </option>
          </SelectControl>
          <Text mt={2} fontSize="sm" fontWeight="400" color="gray.600">
            Schools without configured boundaries will not be filtered out by
            boundary rules.
          </Text>
        </>
      )}
    </Flex>
  );
};
